import { useEffect, useState } from "react";
import type { FieldValues, UseFormReturn } from "react-hook-form";

// A primitive derived from the form values. Subscribed outside render, like useFieldsInError, so the
// host re-renders only when the derived value changes - useWatch({ control }) instead re-renders it on
// every change of any field. derive must be stable (or memoized): a new one subscribes again.
export const useDerivedFormValue = <TForm extends FieldValues, T extends string | number | boolean | null>(
  formMethods: Pick<UseFormReturn<TForm>, "getValues" | "subscribe">,
  derive: (values: TForm) => T,
): T => {
  const { getValues, subscribe } = formMethods;
  const [derivedValue, setDerivedValue] = useState(() => derive(getValues()));

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ values }) => setDerivedValue(derive(values)),
    });
    // Catches values changed between the render and this subscription, or a new derive.
    setDerivedValue(derive(getValues()));

    return unsubscribe;
  }, [derive, getValues, subscribe]);

  return derivedValue;
};
